import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { UserRankInfo } from "@/lib/api/leaderboard";
import { TrendingUp, Award, Target, Trophy } from "lucide-react";

interface UserStatsCardProps {
  userRank: UserRankInfo | null;
  isLoading?: boolean;
  companyName?: string;
  isCompanyScoped?: boolean;
}

export function UserStatsCard({ userRank, isLoading, companyName, isCompanyScoped }: UserStatsCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 animate-pulse">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!userRank) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Complete a simulation to see your statistics
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-primary" />
          {isCompanyScoped && companyName 
            ? `Your Rank in ${companyName}`
            : 'Your Statistics'
          }
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Rank and Percentile */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Your Rank</span>
            <span className="text-2xl font-bold text-primary">
              #{userRank.rank}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">out of {userRank.total_users} users</span>
            <span className="text-primary font-medium">
              Top {userRank.percentile}%
            </span>
          </div>
          <Progress value={100 - userRank.percentile} className="h-2" />
        </div>

        {/* Performance Stats */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t">
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Trophy className="h-3 w-3" />
              <span className="text-xs">Best Score</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              {userRank.best_score}
            </p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <TrendingUp className="h-3 w-3" />
              <span className="text-xs">Avg Score</span>
            </div>
            <p className="text-2xl font-bold">
              {userRank.average_score?.toFixed(1) || 'N/A'}
            </p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Target className="h-3 w-3" />
              <span className="text-xs">Sessions</span>
            </div>
            <p className="text-2xl font-bold">
              {userRank.total_sessions}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
